/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {client, providers} from '@logilab/cwclientlibjs';
import {
    ValidationError,
    getDisplayText,
    AttributeValueEvent,
    defaultAttributeValidation,
    TranslationContext,
    saveNewAttributeValue,
} from './commons';

/**
 * Base interface for components for entity attributes
 */
export class EntityAttribute<
    P extends EntityAttributeProps = EntityAttributeProps,
    S extends EntityAttributeState = EntityAttributeState
> extends React.Component<P, S> {
    public static contextType = TranslationContext;
    public context!: React.ContextType<typeof TranslationContext>;
    /**
     * Whether the component is currently mounted
     */
    _isMounted: boolean;
}

/**
 * Enum to change rendering if status changed
 * when saving attribute
 */
export enum EntityAttributeStateEnum {
    idle, // attribute value has not been changed
    changed, // attribute balue has been changed
    saving, // attribute is currently saving
    error, // attribute did not save because error
}

/**
 * The parameters for attribute components
 */
export interface EntityAttributeProps {
    /**
     * The entity
     */
    entity: providers.Entity;
    /**
     * The schema of the attribute
     */
    attribute: providers.EntityAttributeSchema;
    /**
     * Getter to retrieve the attribute's value on the entity
     */
    getter: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema
    ) => any;
    /**
     * Whether the use can edit the attribute's value
     */
    disabled: boolean;
    /**
     * Whether a value for the attribute is required
     */
    isRequired: (attribute: providers.EntityAttributeSchema) => boolean;
    /**
     * The CSS class name(s) for the component in normal conditions
     */
    className: string;
    /**
     * The CSS class name(s) for the component when the attribute's value is invalid
     */
    invalidClassName: string;
    /**
     * Handler of events when a new value input has been received
     */
    onChange: AttributeValueEvent;
    /**
     * The RqlClient to modify the attribute if given
     */
    rqlClient?: client.RqlClient;
    /**
     * Tries to validate the new value for the attribute
     */
    validateValue: AttributeValueEvent;
    /**
     * Event when the input for the attribute is changed by the user
     * @param component The current component (this)
     * @param event The original HTML input event
     */
    onFieldChange: (
        component: EntityAttribute,
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    /**
     * Event when the input component lose focus
     * @param component The current component (this)
     * @param event The original HTML input event
     */
    onFieldBlur: (
        component: EntityAttribute,
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    /**
     * Event when the input from the user ended
     * @param component The current component (this)
     * @param input The original input in the field
     */
    onFieldInputEnded: (component: EntityAttribute, input: string) => void;
    /**
     * Event when the value for the attribute is changed by the user
     * @param component The current component (this)
     * @param value The new value
     */
    onFieldEditedValue: (component: EntityAttribute, value: any) => void;
    /**
     * Event when the save button is clicked
     * @param component The current component (this)
     * @param value the to be saved value
     */
    onSaveClicked: (component: EntityAttribute, value: any) => void;
    /**
     * Handler when an attribute has been saved in the database
     * if editable is true
     */
    attributeHasBeenSaved: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any
    ) => void;
    /**
     * Handler when an attribute has not been saved in the database
     * because of an error
     * if editable is true
     */
    attributeSavingError: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any,
        error: string
    ) => void;
    /**
     * Renders the title for the attribute
     * @param component The current component (this)
     */
    renderTitle: (component: EntityAttribute) => JSX.Element;
    /**
     * Renders the input field for the attribute
     * @param component The current component (this)
     */
    renderInput: (component: EntityAttribute) => JSX.Element;
    /**
     * Renders the save button for the attribute
     * @param component The current component (this)
     */
    renderSaveButton: (component: EntityAttribute) => JSX.Element;
    /**
     * Renders the validation message for the attribute
     * @param component The current component (this)
     */
    renderValidation: (component: EntityAttribute) => JSX.Element | null;
    /**
     * Renders the component
     * @param component The current component (this)
     */
    render: (component: EntityAttribute) => JSX.Element;
}

/**
 * The parameters for attribute with unit components
 */
export interface EntityAttributeWithStaticUnitProps
    extends EntityAttributeProps {
    /**
     * The name of the current user unit
     */
    unitName: string;
    /**
     * Converts the value expressed in the field storage unit into the value expressed in the current user unit
     */
    toCurrentUnit: (value: any) => any;
    /**
     * Converts the value expressed in the current user unit into the value expressed in the field storage unit
     */
    toStorageUnit: (value: number) => number;
}

/**
 * State for this component
 */
export interface EntityAttributeState {
    /**
     * The referenced value in database
     */
    valueReference: string | number | boolean | null | undefined;
    /**
     * The current value for the input field
     */
    value: string | number | boolean | null | undefined;
    /**
     * The current validation error, if any
     */
    validationError: ValidationError | null;
    /**
     * The EntityAttribute state if saving is enable
     */
    savingState: EntityAttributeStateEnum;
}

/**
 * The values for the default parameters of attribute components
 */
export const ENTITY_ATTRIBUTE_DEFAULT_PROPS = {
    getter: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema
    ) => entity[attribute.name],
    disabled: false,
    isRequired: (attribute: providers.EntityAttributeSchema) =>
        attribute.cardinality ===
            providers.EntityRelationSchemaCardinality.ExactlyOne ||
        attribute.cardinality ===
            providers.EntityRelationSchemaCardinality.OneOrMore,
    className: '',
    invalidClassName: '',
    validateValue: defaultAttributeValidation,
    onFieldChange: (
        component: EntityAttribute,
        event: React.FormEvent<HTMLInputElement>
    ) => {
        if (component.props.disabled === true) {
            return;
        }
        let input = event.currentTarget.value;
        component.setState({value: input});
        component.props.onFieldInputEnded(component, event.currentTarget.value);
    },
    onFieldBlur: (
        component: EntityAttribute,
        event: React.FormEvent<HTMLInputElement>
    ) => {
        if (!component._isMounted || component.props.disabled === true) {
            // component is not mounted, or is read-only
            return;
        }
        component.props.onFieldInputEnded(component, event.currentTarget.value);
    },
    onFieldInputEnded: (component: EntityAttribute, input: string) => {
        if (
            input.length === 0 &&
            !component.props.isRequired(component.props.attribute)
        ) {
            // the new value is empty and not required
            // => convert to unset value
            return component.props.onFieldEditedValue(component, null);
        }
        component.props.onFieldEditedValue(component, input);
    },
    onFieldEditedValue: (component: EntityAttribute, value: any) => {
        component.setState({value});
        const oldValue = component.state.valueReference;
        if (oldValue === value && component.state.validationError === null) {
            // no change and no previous error
            return;
        }
        let error = component.props.validateValue(
            component.props.entity,
            component.props.attribute,
            component.props.isRequired(component.props.attribute),
            value
        );
        component.setState({validationError: error});
        if (error !== null) {
            return;
        }
        component.setState({
            savingState: EntityAttributeStateEnum.changed,
        });
        component.props.onChange(
            component.props.entity,
            component.props.attribute,
            component.props.isRequired(component.props.attribute),
            value,
            component.props.rqlClient
        );
    },
    onSaveClicked: (component: EntityAttribute, value: any) => {
        component.setState({
            savingState: EntityAttributeStateEnum.saving,
        });
        saveNewAttributeValue(
            component.props.entity,
            component.props.attribute,
            component.props.isRequired(component.props.attribute),
            value,
            component.props.rqlClient
        )
            .then(() => {
                component.setState({
                    valueReference: value,
                    savingState: EntityAttributeStateEnum.idle,
                });
                component.props.attributeHasBeenSaved(
                    component.props.entity,
                    component.props.attribute,
                    value
                );
            })
            .catch((reason) => {
                component.setState({
                    savingState: EntityAttributeStateEnum.error,
                });
                component.props.attributeSavingError(
                    component.props.entity,
                    component.props.attribute,
                    value,
                    reason
                );
            });
    },
    attributeHasBeenSaved: (
        _entity: providers.Entity,
        _attribute: providers.EntityAttributeSchema,
        _value: any
    ) => {},
    attributeSavingError: (
        _entity: providers.Entity,
        _attribute: providers.EntityAttributeSchema,
        _value: any,
        _error: string
    ) => {},
    renderTitle: (component: EntityAttribute) => {
        return (
            <span>
                {component.context.translate(component.props.attribute.name)}
            </span>
        );
    },
    renderInput: (component: EntityAttribute) => {
        let text = getDisplayText(component.state.value);
        return (
            <input
                type={'text'}
                disabled={component.props.disabled}
                required={component.props.isRequired(component.props.attribute)}
                value={text}
                onChange={(event) =>
                    component.props.onFieldChange(component, event)
                }
                onBlur={(event) =>
                    component.props.onFieldBlur(component, event)
                }
            />
        );
    },
    renderSaveButton: (component: EntityAttribute) => {
        const props = component.props;
        const state = component.state;
        if (props.disabled || state.value === state.valueReference) {
            return <></>;
        }
        if (state.savingState === EntityAttributeStateEnum.saving) {
            return <>{component.context.translate('loading')} ...</>;
        }
        if (state.savingState === EntityAttributeStateEnum.error) {
            return <>{component.context.translate('error')}</>;
        }

        return (
            <button
                onClick={() => {
                    component.props.onSaveClicked(component, state.value);
                }}
            >
                {component.context.translate('save')}
            </button>
        );
    },
    renderValidation: (component: EntityAttribute) => {
        return component.state.validationError !== null ? (
            <span>
                <br />
                {component.state.validationError.parameters !== undefined
                    ? component.context.translate(
                          component.state.validationError.message,
                          ...component.state.validationError.parameters
                      )
                    : component.context.translate(
                          component.state.validationError.message
                      )}
            </span>
        ) : null;
    },
    render: (component: EntityAttribute) => {
        return (
            <tr
                className={
                    component.state.validationError !== null
                        ? component.props.invalidClassName
                        : component.props.className
                }
            >
                <td className="title">
                    {component.props.renderTitle(component)}
                </td>
                <td>
                    {component.props.renderInput(component)}
                    {component.props.renderValidation(component)}
                    {component.props.renderSaveButton(component)}
                </td>
            </tr>
        );
    },
};

/**
 * Builds a state from the given props
 * @param props The current props
 */
export function getEntityAttributeStateFromProps(
    props: EntityAttributeProps
): EntityAttributeState {
    let value = props.getter(props.entity, props.attribute);
    return {
        value: value,
        valueReference: value,
        validationError: props.validateValue(
            props.entity,
            props.attribute,
            props.isRequired(props.attribute),
            value
        ),
        savingState: EntityAttributeStateEnum.idle,
    };
}
